import React, { useCallback } from 'react';
import { StyleSheet, View } from 'react-native';
import { BodyText, FadeView, Switch, TextField } from '@sweetgreen/garnish';

import { useLocalizationContext } from '@order/Localization';

import { CREDIT_CARDS_ICONS } from '../../../constants/credit-cards';
import type {
  ControlledFormProps,
  CreditCardField,
} from '../CreditCardForm.types';
import { BlockedFields } from './CreditCardForm.blocked';
import { Footer, Header, LineItem } from './CreditCardForm.components';
import { LoadingFields } from './CreditCardForm.loading';

// Section of the credit card form controlled by us instead of Braintree.
export const CreditCardFormControlledContent = ({
  children,
  account,
  fadeDuration,
  subtitle,
  isSaving,
  isEditing,
  isDeleting,
  isLoadingFields,
  canSubmitForm,
  form,
  onFormChange,
  onSubmitForm,
  onDelete,
}: ControlledFormProps) => {
  const { t } = useLocalizationContext();

  // ─────────────────────── Callbacks ──────────────────────────────────
  const updateFormField = useCallback(
    (field: CreditCardField) => (value: string | number | boolean) => {
      onFormChange({ ...form, [field]: value });
    },
    [form, onFormChange],
  );

  // ─────────────────────── Render ──────────────────────────────────
  return (
    <View style={styles.spacedColumn}>
      <View style={styles.column}>
        <Header subtitle={subtitle} isEditing={isEditing} />

        {/* Loading placeholders for each field */}
        <FadeView duration={fadeDuration} show={isLoadingFields}>
          {!isEditing && <LoadingFields />}
        </FadeView>

        {/* Section Controlled by Braintree */}
        {children}

        {/* Used when editing credit card, fields disabled */}
        <BlockedFields
          show={Boolean(isEditing)}
          account={account}
          cardType={CREDIT_CARDS_ICONS[account?.cardType ?? 'NEW']}
        />

        {/* Nickname field, not controlled by Braintree */}
        <LineItem>
          <TextField
            testID="account.credit-card-form.nickname"
            label={t('account.credit-card-form.nickname')}
            value={form.nickname}
            onChangeText={updateFormField('nickname')}
          />
        </LineItem>

        {/* Default credit card switch, not controlled by Braintree */}
        <LineItem style={styles.marginBottom}>
          <View style={styles.row}>
            <View style={styles.column}>
              <BodyText size={3}>
                {t('account.credit-card-form.default')}
              </BodyText>
            </View>
            <Switch
              testID="account.credit-card-form.default"
              value={form.default}
              onChange={updateFormField('default')}
            />
          </View>
        </LineItem>
      </View>

      {/* Save / Remove footer, not controlled by Braintree */}
      <Footer
        isEditing={isEditing}
        isSaving={isSaving}
        isDeleting={isDeleting}
        canSubmit={canSubmitForm}
        onDelete={onDelete}
        onSubmit={onSubmitForm}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
  },
  column: {
    flex: 1,
  },
  spacedColumn: {
    flex: 1,
    flexGrow: 1,
    justifyContent: 'space-between',
  },
  marginBottom: {
    marginBottom: 0,
  },
});
